// The one clock.
//
// t is seconds TDB from J2000, rate is simulated seconds per real second, and
// everything that used to keep its own time base reads this: sun direction,
// body positions and rotation, terminators, plot insolation, the Atlas era
// slider (docs/roadmap/01-world-model.md, "Ephemerides and time").
//
// The Sol and Mars Year display is the HUD's, moved here unchanged in output
// and changed in what drives it: it used to accumulate 0.0005 sols per real
// second with no epoch underneath, so the era it displayed and the era the
// Atlas drew were unrelated numbers. Here the sol is a function of t, which
// makes the Earth date the date of t by construction.
//
// Pure but for Date, so this file strips and runs under Node.

const SOL_BASE = 112;
const MARS_YEAR_BASE = 32;
/** Canonical sols per Mars year. */
const MARS_YEAR_SOLS = 668.5921;
/** Canonical Mars sol: 24h 39m 35.244s, in Earth days. */
const SOL_DAYS = 1.0274912517;
export const SOL_SECONDS = SOL_DAYS * 86400;
/** Canonical sols per Mars year, for anything deriving a season from t. */
export const SOLS_PER_MARS_YEAR = MARS_YEAR_SOLS;

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Mission start, the Esperanca Window: canon tabletop/modules/01, section 3. */
const EARTH_ANCHOR_MS = Date.UTC(2078, 4, 9);
const J2000_MS = Date.UTC(2000, 0, 1, 12, 0, 0);

export const J2000_JD = 2451545.0;

/**
 * TAI-UTC in whole seconds, from the UTC instant each took effect: the IERS
 * leap second list (IANA leap-seconds.list, Bulletin C). Each entry is the 1st
 * of its month at 00:00:00 UTC.
 */
const LEAP_SECONDS: readonly (readonly [number, number])[] = [
  [Date.UTC(1972, 0, 1), 10],
  [Date.UTC(1972, 6, 1), 11],
  [Date.UTC(1973, 0, 1), 12],
  [Date.UTC(1974, 0, 1), 13],
  [Date.UTC(1975, 0, 1), 14],
  [Date.UTC(1976, 0, 1), 15],
  [Date.UTC(1977, 0, 1), 16],
  [Date.UTC(1978, 0, 1), 17],
  [Date.UTC(1979, 0, 1), 18],
  [Date.UTC(1980, 0, 1), 19],
  [Date.UTC(1981, 6, 1), 20],
  [Date.UTC(1982, 6, 1), 21],
  [Date.UTC(1983, 6, 1), 22],
  [Date.UTC(1985, 6, 1), 23],
  [Date.UTC(1988, 0, 1), 24],
  [Date.UTC(1990, 0, 1), 25],
  [Date.UTC(1991, 0, 1), 26],
  [Date.UTC(1992, 6, 1), 27],
  [Date.UTC(1993, 6, 1), 28],
  [Date.UTC(1994, 6, 1), 29],
  [Date.UTC(1996, 0, 1), 30],
  [Date.UTC(1997, 6, 1), 31],
  [Date.UTC(1999, 0, 1), 32],
  [Date.UTC(2006, 0, 1), 33],
  [Date.UTC(2009, 0, 1), 34],
  [Date.UTC(2012, 6, 1), 35],
  [Date.UTC(2015, 6, 1), 36],
  [Date.UTC(2017, 0, 1), 37],
];

/**
 * TT minus UTC in seconds at a UTC instant: 32.184 s of TT over TAI plus the
 * TAI-UTC in force. Before the table's first row it reads that row's own value
 * (10 s) and past its last it holds 37: both ends are clamps, and Horizons is
 * read only from 1972-01-01T00:00:01 on, where the table begins.
 */
function ttMinusUtcS(utcMs: number): number {
  let taiMinusUtc = LEAP_SECONDS[0]?.[1] ?? 0;
  for (const [from, seconds] of LEAP_SECONDS) {
    if (utcMs >= from) taiMinusUtc = seconds;
  }
  return 32.184 + taiMinusUtc;
}

/**
 * t of the display anchor: 2078-05-09 00:00 UTC, the Esperanca Window, which
 * is Sol 112 / MY 32 on the HUD. It is a TDB instant, the TT-UTC offset at
 * that date (69.184 s) further from J2000 than the UTC milliseconds alone.
 */
export const MISSION_ANCHOR_T = tFromEarthDateMs(EARTH_ANCHOR_MS);

/**
 * Real time: one simulated second a real second, and where the clock boots.
 *
 * Pillar B's 60:1 Universe Clock (../4xciv/02-design-pillars.md) is not gone -
 * it is the "1 MIN/S" level below, one press away. What changed is who picks:
 * the pace is the player's, so the clock starts at the pace a player who has
 * picked nothing would expect.
 */
export const DEFAULT_RATE = 1;

/** The one rate every automatic drop lands on, inside a single tick. */
export const REAL_TIME_RATE = 1;

/** One step of the warp control, in the order it steps through. */
export interface WarpLevel {
  /** Simulated seconds per real second. */
  readonly rate: number;
  /** What the readout says, in the player's own terms. */
  readonly label: string;
  /** The button's own short face. */
  readonly short: string;
}

/**
 * The levels the player picks between. Fixed steps rather than a scrub: a
 * ratio is not something anyone should have to think about, and each of these
 * says what a second of real time buys.
 */
export const WARP_LEVELS: readonly WarpLevel[] = [
  { rate: REAL_TIME_RATE, label: 'REAL TIME', short: 'REAL' },
  { rate: 60, label: '1 MIN/S', short: 'MIN' },
  { rate: 3600, label: '1 HR/S', short: 'HR' },
  { rate: 86400, label: '1 DAY/S', short: 'DAY' },
];

const YEAR_S = 365.25 * 86400;

/** A rate as game time a real second, in the largest unit that reads as a small
 *  number, two figures: "1x", "12 min/s", "4.2 h/s", "230 d/s", "1.4 yr/s". */
export function clockRateText(rate: number): string {
  if (!Number.isFinite(rate) || rate < 0) return '—';
  const n = (v: number): string => (v >= 9.95 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, ''));
  if (rate < 59.5) return `${n(rate)}x`;
  if (rate < 3570) return `${n(rate / 60)} min/s`;
  if (rate < 85_700) return `${n(rate / 3600)} h/s`;
  if (rate < YEAR_S * 0.995) return `${n(rate / 86400)} d/s`;
  return `${n(rate / YEAR_S)} yr/s`;
}

/** Real seconds from real time to the top level, which is the whole ramp. */
export const WARP_RAMP_SECONDS = 2;

/**
 * The ramp is GEOMETRIC - a constant proportional change, `d(ln rate)/dt` - so
 * the sky and the orrery accelerate rather than snap, and every 60x step costs
 * the same `ln 60 / ln 86400` of the ramp whichever end of the range it sits
 * at. A linear ramp over the same 2 s would spend its first frame going from
 * 1x to 700x, which is the jump it was meant to avoid.
 *
 * Up and down both ease. The automatic DROP does not: it is a single
 * assignment (`dropToRealTime`), because a ship a few seconds from a body has
 * no seconds to spend easing out of a day a second.
 */
export const WARP_RAMP_NEPERS_PER_S = Math.log(86400) / WARP_RAMP_SECONDS;

export function jdTdbFromT(t: number): number {
  return J2000_JD + t / 86400;
}

/**
 * A UTC calendar date as sim time. J2000_MS is 12:00 on the TT axis, which is
 * 11:58:55.816 UTC, so its own TT-UTC offset is already inside the
 * subtraction; TDB-TT stays ignored, being under 2 ms.
 */
export function tFromEarthDateMs(ms: number): number {
  return (ms + ttMinusUtcS(ms) * 1000 - J2000_MS) / 1000;
}

/**
 * The inverse, and it has to iterate: the offset is a function of the UTC
 * instant being solved for, so each pass re-reads it there. The offset only
 * moves at a leap boundary, so three passes are more than a round trip needs.
 * In the second after a leap second it is not invertible - POSIX ms has no
 * 23:59:60 - so a t inside that second reads one second early, and exact
 * everywhere else.
 */
export function earthDateMsFromT(t: number): number {
  const ttMs = J2000_MS + t * 1000;
  let utcMs = ttMs - ttMinusUtcS(ttMs) * 1000;
  utcMs = ttMs - ttMinusUtcS(utcMs) * 1000;
  return ttMs - ttMinusUtcS(utcMs) * 1000;
}

const ISO_UTC = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?Z)?$/;

/**
 * An ISO 8601 UTC date or instant (`2024-04-08`, `2024-04-08T18:00Z`,
 * `2016-12-31T23:59:60Z`) as sim time, or null for anything else: an offset,
 * a calendar day that does not exist, or a :60 where UTC has no leap second.
 * A bare date is 00:00:00 UTC.
 */
export function tFromIsoUtc(iso: string): number | null {
  const m = ISO_UTC.exec(iso.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const [h, mi, s] = [Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0)];
  const leap = s === 60;
  const msPart = Number((m[7] ?? '0').padEnd(3, '0'));
  const ms = Date.UTC(y, mo - 1, d, h, mi, leap ? 59 : s, msPart);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d
    || back.getUTCHours() !== h || back.getUTCMinutes() !== mi || (!leap && back.getUTCSeconds() !== s)) return null;
  if (!leap) return tFromEarthDateMs(ms);
  if (!LEAP_SECONDS.slice(1).some(([from])=> from === Date.UTC(y, mo - 1, d, h, mi, 60))) return null;
  return tFromEarthDateMs(ms) + 1;
}

/**
 * `tFromIsoUtc`'s inverse as `YYYY-MM-DDTHH:MM:SS.mmmZ`, writing the :60 of a
 * leap second where `earthDateMsFromT` can only read it as :59.
 */
export function isoUtcFromT(t: number): string {
  for (const [from] of LEAP_SECONDS.slice(1)) {
    const tStart = tFromEarthDateMs(from - 1000) + 1;
    if (t >= tStart && t < tStart + 1) {
      const head = new Date(from - 1000).toISOString().slice(0, 17);
      return `${head}60.${String(Math.floor((t - tStart) * 1000)).padStart(3, '0')}Z`;
    }
  }
  return new Date(Math.round(earthDateMsFromT(t))).toISOString();
}

/** Fractional sol since the mission anchor. */
export function solAt(t: number): number {
  return SOL_BASE + (t - MISSION_ANCHOR_T) / SOL_SECONDS;
}

export interface ClockDisplay {
  /** Fractional sol, monotonic across Mars years. */
  readonly sol: number;
  /** Integer sol within the Mars year, as the HUD prints it. */
  readonly displaySol: number;
  readonly marsYear: number;
  /** The HUD string, middle dot and all: "Sol 112 \u00b7 MY 32". */
  readonly label: string;
  /** "09 May 2078", the HUD tooltip. */
  readonly earthDate: string;
}

function earthDateOf(sol: number): string {
  const ms = EARTH_ANCHOR_MS + (sol - SOL_BASE) * SOL_SECONDS * 1000;
  const d = new Date(ms);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = MONTH_NAMES[d.getUTCMonth()] ?? '';
  return `${day} ${month} ${d.getUTCFullYear()}`;
}

export function displayAt(t: number): ClockDisplay {
  const sol = solAt(t);
  const solInt = Math.floor(sol);
  // Math.floor, not Math.trunc: for a negative solInt (a backward scrub past
  // the anchor's year) truncating division rounds toward zero and leaves
  // sol-of-year negative. Flooring both the year count and the remainder
  // keeps displaySol in [0, MARS_YEAR_SOLS) on either side of the anchor.
  const years = Math.floor(solInt / MARS_YEAR_SOLS);
  const marsYear = MARS_YEAR_BASE + years;
  // Both sides of this subtraction have to use the same (unfloored)
  // MARS_YEAR_SOLS, or the sol-of-year creeps by the fractional part -
  // 0.5921 sol - every Mars year.
  const displaySol = Math.floor(solInt - years * MARS_YEAR_SOLS);
  return {
    sol,
    displaySol,
    marsYear,
    label: `Sol ${displaySol} \u00b7 MY ${marsYear}`,
    earthDate: earthDateOf(sol),
  };
}

export interface ClockConfig {
  /** Seconds TDB from J2000; defaults to the mission anchor. */
  readonly t?: number;
  /** Simulated seconds per real second; defaults to DEFAULT_RATE. */
  readonly rate?: number;
}

export interface Clock {
  /** Seconds TDB from J2000. */
  readonly t: number;
  /** Simulated seconds per real second. Ramps toward `warpTarget`. */
  readonly rate: number;
  /** The level that was picked. `rate` eases onto it over the ramp. */
  readonly warpTarget: number;
  /** Set rate and target together: no ramp, for a check or a hard reset. */
  setRate(rate: number): void;
  /** Pick a level. The rate eases onto it; nothing jumps. */
  setWarpTarget(rate: number): void;
  /** Real time now, pick and all - what an automatic drop does, in one tick. */
  dropToRealTime(): void;
  /** True while a fight holds the rate at real time; the target survives it. */
  readonly held: boolean;
  /**
   * A fight's own override, distinct from a drop: `true` forces the rate to
   * 1 in one assignment and leaves `target` untouched, so the picked level
   * survives the fight; `false` releases the hold and lets `update` resume
   * ramping `rate` toward that same `target` from the next call.
   */
  holdRealTime(hold: boolean): void;
  setInstant(secondsFromJ2000: number): void;
  /** Move the instant by simulated seconds, leaving the rate alone. */
  scrub(deltaSeconds: number): void;
  jdTdb(): number;
  /** Advance by one frame of real time. A no-op while `paused`. */
  update(dtRealSeconds: number): void;
  display(): ClockDisplay;
  /** True from `pause()` until the matching `resume()`. */
  readonly paused: boolean;
  /** Freeze `t` and the warp ramp where they stand. `update` becomes a no-op. */
  pause(): void;
  /** Undo `pause()`. `t` resumes from exactly where it stopped, no catch-up. */
  resume(): void;
  dispose(): void;
}

export function createClock(config: ClockConfig = {}): Clock {
  let t = config.t ?? MISSION_ANCHOR_T;
  let rate = config.rate ?? DEFAULT_RATE;
  let target = rate;
  let held = false;
  let paused = false;

  return {
    get t(): number {
      return t;
    },
    get rate(): number {
      return rate;
    },
    get warpTarget(): number {
      return target;
    },
    setRate(next: number): void {
      if (!Number.isFinite(next)) throw new Error('clock: rate is not finite');
      rate = next;
      target = next;
    },
    setWarpTarget(next: number): void {
      if (!Number.isFinite(next) || !(next > 0)) throw new Error('clock: warp target is not a rate');
      target = next;
    },
    dropToRealTime(): void {
      rate = REAL_TIME_RATE;
      target = REAL_TIME_RATE;
    },
    get held(): boolean {
      return held;
    },
    holdRealTime(hold: boolean): void {
      held = hold;
      if (held) rate = REAL_TIME_RATE;
    },
    setInstant(secondsFromJ2000: number): void {
      if (!Number.isFinite(secondsFromJ2000)) throw new Error('clock: instant is not finite');
      t = secondsFromJ2000;
    },
    scrub(deltaSeconds: number): void {
      if (!Number.isFinite(deltaSeconds)) throw new Error('clock: scrub is not finite');
      t += deltaSeconds;
    },
    jdTdb(): number {
      return jdTdbFromT(t);
    },
    update(dtRealSeconds: number): void {
      if (paused || !Number.isFinite(dtRealSeconds)) return;
      // Ramp first, then advance: the rate this frame's instant moved at is
      // the rate a caller reads back off the handle afterwards, so a frame's
      // simulated seconds and its share of the clock can never disagree.
      // A held clock skips the ramp outright - `target` sits wherever the
      // fight found it and waits for `holdRealTime(false)` to let it resume.
      if (!held && rate !== target && dtRealSeconds > 0 && rate > 0 && target > 0) {
        const k = Math.exp(WARP_RAMP_NEPERS_PER_S * dtRealSeconds);
        rate = rate < target ? Math.min(target, rate * k) : Math.max(target, rate / k);
      }
      t += rate * dtRealSeconds;
    },
    display(): ClockDisplay {
      return displayAt(t);
    },
    get paused(): boolean {
      return paused;
    },
    pause(): void {
      paused = true;
    },
    resume(): void {
      paused = false;
    },
    dispose(): void {
      // Nothing owned; the handle keeps the repo's createX shape.
    },
  };
}

// ---------- the one instance ----------
//
// main.ts advances it and the Atlas reads and scrubs it. A module-level handle
// rather than an import from main.ts, which would be circular, and rather than
// a global, which the instruments could mistake for an app-side hook.

let shared: Clock | null = null;

/** The game clock. Created on first use so nothing depends on import order. */
export function gameClock(): Clock {
  if (!shared) {
    shared = createClock();
    // A backgrounded tab stops rAF, so the next frame's dt would otherwise be
    // however long the tab was hidden: pausing holds t exactly where it stood
    // instead of jumping the sim forward on return. Orthogonal to main.ts's
    // isMenuOpen() branch, which keeps the clock running by design.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) shared?.pause();
        else shared?.resume();
      });
    }
  }
  return shared;
}
